import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import { step1Questions, worksheetQuestions, additionalAmountInitialQuestion } from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import CollapsibleQuestions from '../CollapsibleQuestions';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function HawaiiForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  const step1Form = useRef();
  const step1InitialQuestions = getInitialQuestions(step1Questions);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitialQuestions);

  const [totalNumberOfAllowances, setTotalNoOfAllowances] = useState(data.total_number_of_allowances || 0);
  const [totalNumberOfAllowancesError, setTotalNumberOfAllowancesError] = useState(false);

  const [claimYourself, setClaimYourself] = useState(data.claim_yourself || false);
  const [claimSingleMarried, setClaimSingleMarried] = useState(data.claim_single_married || false);
  const [claimSpouse, setClaimSpouse] = useState(data.claim_spouse || false);
  const [claimHeadOfHousehold, setClaimHeadOfHousehold] = useState(data.claim_head_of_household || false);
  const [claim250TaxCredits, setClaim250TaxCredits] = useState(data.claim_250_tax_credits || false);

  const [worksheetOpen, setWorksheetOpen] = useState(false);
  const worksheetForm = useRef();
  const worksheetInitialQuestions = getInitialQuestions(worksheetQuestions);
  const [wsQuestions, setWSQuestions] = useState(worksheetInitialQuestions);

  const additionalAmountForm = useRef();
  const additionalAmountQuestionWithAnswer = getInitialQuestions(additionalAmountInitialQuestion);
  const [additionalAmountQuestion, setAdditionalAmountQuestion] = useState(additionalAmountQuestionWithAnswer);

  // to calculate total number of allowances
  const calculateTotalAllowances = () => {
    let value = 0;

    if (claimYourself) value += 1;
    if (claimSingleMarried) value += 1;
    if (claimSpouse) value += 1;

    const claimForAge = wsQuestions[0].answer.answer.claim_for_age;
    switch (claimForAge) {
      case 'claim_for_age_1':
        value += 1;
        break;
      case 'claim_for_age_2':
        value += 2;
        break;
      default:
        break;
    }

    const dep = wsQuestions[1].answer.answer.dependents;
    if (/^\d+$/.test(dep)) value += parseInt(dep);

    if (claimHeadOfHousehold) value += 1;
    if (claim250TaxCredits) value += 1;

    const itemizeOrClaimAdjustments = wsQuestions[2].answer.answer.itemize_or_claim_adjustments;
    if (/^\d+$/.test(itemizeOrClaimAdjustments)) value += parseInt(itemizeOrClaimAdjustments);

    return value;
  };

  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
      <Flex column>
        <S.QuestionWrapper>
          <Flex mb="7px">
            <S.QuestionLabel>
              Total number of allowances you are claiming. Note: Hawaii law does not allow "Exempt" status for withholding purposese.
            </S.QuestionLabel>
          </Flex>
          <Flex mb="10px">
            <S.Input
              type="text"
              value={worksheetOpen ? calculateTotalAllowances() : totalNumberOfAllowances}
              onChange={(e) => setTotalNoOfAllowances(e.target.value)}
              disabled={worksheetOpen || isReviewStep}
            />
          </Flex>
          {totalNumberOfAllowancesError && (
            <Flex justifyContent="flex-start">
              <Text color="red">Please enter a valid response.</Text>
            </Flex>
          )}
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  const renderCollapsibleQuestion = (isReviewStep) => (
    <Flex column>
      <Flex mt="10px">
        <CollapsibleQuestions
          title="If you need to calculate your withholding allowances, use this worksheet."
          handleClick={() => {
            if (!isReviewStep) {
              setWorksheetOpen(!worksheetOpen);
            }
          }}
          open={worksheetOpen || isReviewStep}
        >
          {renderClaimQuestions(isReviewStep)}
          <FormConsumer
            questions={wsQuestions}
            {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
            onQuestionChange={({ question, value }) => {
              const questions = wsQuestions.map((q) => {
                if (q.id !== question.id) return q;
                const answer = {
                  ...q.answer,
                  answer: {
                    ...q.answer.answer,
                    [q.id]: value,
                  },
                };
                const updatedQuestion = { ...q, answer };
                return updatedQuestion;
              });
              setWSQuestions(questions);
            }}
            ref={worksheetForm}
          />
        </CollapsibleQuestions>
      </Flex>
    </Flex>
  );

  const renderClaimQuestions = (isReviewStep) => (
    <Flex column mb="20px">
      <S.QuestionWrapper>
        <Flex mb="7px">
          <S.QuestionLabel>
            Check the following boxes that apply to you:
          </S.QuestionLabel>
        </Flex>
        <Flex mb="8px">
          <label>
            <Flex>
              <S.Input
                style={{ height: '20px' }}
                type="checkbox"
                value={claimYourself}
                onChange={(e) => setClaimYourself(e.target.checked)}
                checked={claimYourself}
                disabled={isReviewStep}
              />
              <Box ml={2} />
              For yourself if no one else can claim you as a dependent
            </Flex>
          </label>
        </Flex>
        <Flex mb="8px">
          <label>
            <Flex>
              <S.Input
                style={{ height: '20px' }}
                type="checkbox"
                value={claimSingleMarried}
                onChange={(e) => setClaimSingleMarried(e.target.checked)}
                checked={claimSingleMarried}
                disabled={isReviewStep}
              />
              <Box ml={2} />
              If you are single and have only one job OR you are married, have only one job, and your spouse does not work
            </Flex>
          </label>
        </Flex>
        <Flex mb="8px">
          <label>
            <Flex>
              <S.Input
                style={{ height: '20px' }}
                type="checkbox"
                value={claimSpouse}
                onChange={(e) => setClaimSpouse(e.target.checked)}
                checked={claimSpouse}
                disabled={isReviewStep}
              />
              <Box ml={2} />
              For your spouse, if no one else can claim your spouse as a dependent AND your spouse is not claiming a withholding allowance for himself or herself on a Form HW-4
            </Flex>
          </label>
        </Flex>
        <Flex mb="8px">
          <label>
            <Flex>
              <S.Input
                style={{ height: '20px' }}
                type="checkbox"
                value={claimHeadOfHousehold}
                onChange={(e) => setClaimHeadOfHousehold(e.target.checked)}
                checked={claimHeadOfHousehold}
                disabled={isReviewStep}
              />
              <Box ml={2} />
              If you will file as head of household on your tax return
            </Flex>
          </label>
        </Flex>
        <Flex mb="8px">
          <label>
            <Flex>
              <S.Input
                style={{ height: '20px' }}
                type="checkbox"
                value={claim250TaxCredits}
                onChange={(e) => setClaim250TaxCredits(e.target.checked)}
                checked={claim250TaxCredits}
                disabled={isReviewStep}
              />
              <Box ml={2} />
              If you estimate that you will have at least $250 of total tax credits
            </Flex>
          </label>
        </Flex>
      </S.QuestionWrapper>
    </Flex>
  );

  const renderAdditionalAmount = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={additionalAmountQuestion}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = additionalAmountQuestion.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAdditionalAmountQuestion(questions);
        }}
        ref={additionalAmountForm}
      />
    </Flex>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {renderCollapsibleQuestion()}
          {renderAdditionalAmount()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (step1Form && step1Form.current) {
              step1Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (additionalAmountForm && additionalAmountForm.current) {
              additionalAmountForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (worksheetOpen) {
              if (worksheetForm && worksheetForm.current) {
                worksheetForm.current.validate((error) => {
                  if (error) err = error;
                });
              }
            } else if (!(/^\d*$/.test(totalNumberOfAllowances))) {
              err = true;
              setTotalNumberOfAllowancesError(true);
            } else {
              setTotalNumberOfAllowancesError(false);
            }
            if (err) {
              return;
            }
            // No error, proceed to save
            // - save answers to server
            // - processed to next step
            const stepOneData = stepOneQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            const additionalAmountData = additionalAmountQuestion.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            let totalAllowancesTemp = 0;
            let claimYourselfTemp = false;
            let claimSingleMarriedTemp = false;
            let claimSpouseTemp = false;
            let claimHeadOfHouseholdTemp = false;
            let claim250TaxCreditsTemp = false;
            let wsData;
            if (worksheetOpen) {
              totalAllowancesTemp = calculateTotalAllowances();
              claimYourselfTemp = claimYourself;
              claimSingleMarriedTemp = claimSingleMarried;
              claimSpouseTemp = claimSpouse;
              claimHeadOfHouseholdTemp = claimHeadOfHousehold;
              claim250TaxCreditsTemp = claim250TaxCredits;
              wsData = wsQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            } else {
              totalAllowancesTemp = totalNumberOfAllowances;
              wsData = worksheetInitialQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            }
            const claimData = {
              claim_yourself: claimYourselfTemp,
              claim_single_married: claimSingleMarriedTemp,
              claim_spouse: claimSpouseTemp,
              claim_head_of_household: claimHeadOfHouseholdTemp,
              claim_250_tax_credits: claim250TaxCreditsTemp,
            };
            const totalAllowancesData = { total_number_of_allowances: totalAllowancesTemp.toString() };
            const data = {
              ...stepOneData, ...claimData, ...wsData, ...totalAllowancesData, ...additionalAmountData,
            };
            saveFormData(data, () => {
              setCurrentStep(STEPS.step2);
              scrollTop();
            });
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {renderCollapsibleQuestion(true)}
        {renderAdditionalAmount(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );
  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default HawaiiForm;
